import { Link } from "wouter";
import { useSafeBrowserLocation, useSafeSearch, useSafeFragment } from "../lib/router-safe";
import { Logo } from "../lib/brand";
import { postHref } from "../lib/postNavigation";
import { useI18n } from "../lib/i18n";
import { BUILT_RELEASE_VERSION } from "../lib/appVersion";

const cols = [
  { h: "For Job Seekers", hk: "footer.col.jobSeekers", items: ["Browse Jobs", "Manage Profile", "How It Works", "Career Resources"] },
  { h: "For Professionals", hk: "footer.col.professionals", items: ["Browse Services", "List a Service", "Tips & Guides", "Help Center"] },
  { h: "For Companies", hk: "footer.col.companies", items: ["Post a Job", "Company Profiles", "Service Requests", "Projects & Bidding"] },
  { h: "About KontrataPH", hk: "footer.col.about", items: ["About Us", "Our Mission", "Trust & Safety", "Legal hub", "Changelog", "Terms of Service", "Privacy Policy"] },
];
// [#13] English label -> i18n key, so footer links translate while destinations stay fixed.
const ITEM_KEYS: Record<string, string> = {
  "Browse Jobs": "footer.item.browseJobs", "How It Works": "footer.item.howItWorks", "Career Resources": "footer.item.careerResources",
  "Browse Services": "footer.item.browseServices", "List a Service": "footer.item.listService", "Tips & Guides": "footer.item.tipsGuides", "Help Center": "footer.item.helpCenter",
  "Post a Job": "footer.item.postJob", "Company Profiles": "footer.item.companyProfiles", "Service Requests": "footer.item.serviceRequests", "Projects & Bidding": "footer.item.projectsBidding",
  "About Us": "footer.item.aboutUs", "Our Mission": "footer.item.ourMission", "Trust & Safety": "footer.item.trustSafety", "Terms of Service": "footer.item.terms", "Privacy Policy": "footer.item.privacy",
};

// Every footer label maps to a real destination (no dead links).
const LINKS: Record<string, string> = {
  // For Job Seekers
  "Browse Jobs": "/marketplace?tab=Jobs",
  "Manage Profile": "/worker-profile",
  "How It Works": "/how-it-works",
  "Career Resources": "/how-it-works",
  // For Professionals
  "Browse Services": "/marketplace?tab=Services",
  "List a Service": "/post/service",
  "Tips & Guides": "/how-it-works",
  "Help Center": "/support",
  // For Companies
  "Post a Job": "/post/job",
  "Company Profiles": "/marketplace?tab=Companies",
  "Service Requests": "/marketplace?tab=RFQs",
  "Projects & Bidding": "/marketplace?tab=Project%20Bidding",
  // About
  "About Us": "/how-it-works",
  "Our Mission": "/how-it-works",
  "Trust & Safety": "/safety",
  "Legal hub": "/legal",
  "Changelog": "/changelog",
  "Terms of Service": "/legal?document=terms",
  "Privacy Policy": "/legal?document=privacy",
};
const POST_TYPES: Record<string, string> = { "List a Service": "service", "Post a Job": "job" };

export function Footer() {
  const { t } = useI18n();
  const [location] = useSafeBrowserLocation();
  const search = useSafeSearch();
  const fragment = useSafeFragment();
  const returnTo = (search ? `${location}?${search}` : location) + fragment;
  return (
    <footer className="hidden sm:block bg-navy text-white/80 mt-8">
      <div className="container-x py-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-5">
        <div className="lg:col-span-1">
          <Logo light withTagline />
          <p className="text-sm text-white/60 mt-3 max-w-xs">
            {t("footer.tagline")}
          </p>
        </div>
        {cols.map((c) => (
          <div key={c.h}>
            <h4 className="text-white font-bold text-sm mb-3">{t(c.hk)}</h4>
            <ul className="space-y-2 text-sm">
              {c.items.map((i) => (
                <li key={i}><Link href={POST_TYPES[i] ? postHref(POST_TYPES[i], returnTo) : LINKS[i] || "/marketplace"} className="hover:text-white">{t(ITEM_KEYS[i] || "", i)}</Link></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10">
        <div className="container-x py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <span>© 2026 KontrataPH. All rights reserved. <span className="text-white/60">v{BUILT_RELEASE_VERSION}</span></span>
          <span>Trabaho. Serbisyo. Negosyo. Kontrata.</span>
        </div>
      </div>
    </footer>
  );
}
